import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/lib/i18n";

function LangToggle({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className={`flex items-center border border-[#272B36] ${className}`}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 font-mono-tech text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
            lang === l ? "bg-[#E65100] text-white" : "text-[#64748B] hover:text-[#F8FAFC]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#272B36] bg-[#0D0E11]/85 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <button
          data-testid="navbar-brand-logo"
          onClick={() => go("#home")}
          className="flex items-center gap-3 text-left"
        >
          <span className="flex h-9 w-9 items-center justify-center bg-[#E65100] font-display text-xl font-bold text-white">
            F
          </span>
          <span className="leading-tight">
            <span className="block font-display text-xl font-bold uppercase leading-none tracking-wide text-[#F8FAFC]">
              Forte Servizii
            </span>
            <span className="mt-1 block font-mono-tech text-[10px] leading-none tracking-[0.35em] text-[#64748B]">
              {t.nav.tagline}
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-6 lg:flex">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="font-mono-tech text-xs font-medium uppercase tracking-[0.18em] text-[#94A3B8] transition-colors hover:text-[#F97316]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <span className="hidden items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#64748B] 2xl:flex">
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-[#22c55e]" />
            {t.nav.status}
          </span>
          <LangToggle />
          <a
            data-testid="nav-phone-link"
            href="tel:+393931961669"
            className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-[#F8FAFC] transition-colors hover:text-[#F97316]"
          >
            <Phone className="h-4 w-4 text-[#F97316]" strokeWidth={2} />
            +39 393 196 1669
          </a>
          <button
            data-testid="nav-cta-quote-button"
            onClick={() => go("#contatti")}
            className="bg-[#E65100] px-5 py-2.5 font-mono-tech text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#F97316]"
          >
            {t.nav.quote}
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-[#F8FAFC] lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-[#272B36] bg-[#0D0E11] lg:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {t.nav.links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.id)}
                  className="border-b border-[#272B36]/60 py-4 text-left font-mono-tech text-sm uppercase tracking-[0.18em] text-[#94A3B8] last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <LangToggle className="mt-4 w-fit" />
              <button
                data-testid="nav-cta-quote-button-mobile"
                onClick={() => go("#contatti")}
                className="mt-4 bg-[#E65100] px-5 py-3 font-mono-tech text-xs font-bold uppercase tracking-[0.15em] text-white"
              >
                {t.nav.quoteMobile}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
